/**
 * SMS add-on pricing, consent copy and entitlement checks, shared by the
 * SubscriptionCard and the Settings page's add-on card so the price and the
 * legally required purchase disclosure live in one place.
 */

/**
 * Key the backend subscription payload uses to report SMS add-on entitlement.
 */
export const SMS_ADDON_FLAG_KEY = 'has_sms_addon';

/**
 * True when the given payload actually carries the SMS add-on flag.
 * Presence, not truthiness: a payload without the key comes from a backend that
 * does not report - and therefore cannot sell - the add-on, which is a different
 * state from a backend that reports the add-on as off.
 */
export const reportsSmsAddonFlag = (payload) =>
	Boolean(payload) &&
	Object.prototype.hasOwnProperty.call(payload, SMS_ADDON_FLAG_KEY);

/**
 * Recurring list prices of the SMS add-on, as presented to the buyer.
 * Yearly is $214.99 (10% off vs $19.99 x 12).
 */
export const SMS_ADDON_MONTHLY_PRICE_AMOUNT = 19.99;
export const SMS_ADDON_YEARLY_PRICE_AMOUNT = 214.99;
export const SMS_ADDON_MONTHLY_PRICE = `$${SMS_ADDON_MONTHLY_PRICE_AMOUNT.toFixed(2)}`;
export const SMS_ADDON_YEARLY_PRICE = `$${SMS_ADDON_YEARLY_PRICE_AMOUNT.toFixed(2)}`;

/**
 * Recurring SMS add-on cost for one billing period, in dollars.
 */
export const getSmsAddonPeriodCost = (isYearly) =>
	isYearly ? SMS_ADDON_YEARLY_PRICE_AMOUNT : SMS_ADDON_MONTHLY_PRICE_AMOUNT;

/**
 * Monthly-equivalent display amount for the selected SMS interval.
 * Used where upgrade UI quotes everything as a per-month figure.
 */
export const getSmsAddonMonthlyEquivalent = (isYearly) => {
	const amount = isYearly
		? SMS_ADDON_YEARLY_PRICE_AMOUNT / 12
		: SMS_ADDON_MONTHLY_PRICE_AMOUNT;
	return Math.round(amount * 100) / 100;
};

/**
 * Short label for the SMS add-on price on the selected interval.
 */
export const getSmsAddonPriceLabel = (isYearly) =>
	isYearly
		? `${SMS_ADDON_YEARLY_PRICE}/yr`
		: `${SMS_ADDON_MONTHLY_PRICE}/mo`;

/**
 * Consent copy for the standalone SMS add-on purchase.
 * Confirming adds the Stripe subscription item with proration enabled and the
 * backend immediately invoices and pays the resulting balance off-session, so
 * this dialog is the only place the buyer can learn that a charge lands today.
 * It must therefore disclose both the prorated same-day charge and the
 * recurring price, and that the same-day amount varies with the position in
 * the current billing cycle.
 */
export const getSmsAddonPurchaseConsentMessage = (isYearly = false) => {
	const recurring = getSmsAddonPriceLabel(isYearly);
	return (
		'Enable real-time SMS alerts and 2-way reply resolution for your portfolio ' +
		`at ${recurring} recurring. Confirming charges you a ` +
		'prorated amount today for the remainder of your current billing period, ' +
		'in addition to the recurring price. The amount charged today depends on ' +
		'how far you are into the current billing cycle, and the full ' +
		`${recurring} is billed on each renewal after that.`
	);
};

/** @deprecated Prefer getSmsAddonPurchaseConsentMessage(isYearly). */
export const SMS_ADDON_PURCHASE_CONSENT_MESSAGE =
	getSmsAddonPurchaseConsentMessage(false);

/**
 * Confirm-button label for the standalone SMS add-on purchase. It names the
 * same-day prorated charge next to the recurring price so the commit action
 * itself does not read as recurring-only.
 */
export const getSmsAddonPurchaseConfirmLabel = (isYearly = false) =>
	`Yes, Add SMS (${getSmsAddonPriceLabel(isYearly)} + prorated charge today)`;

/** @deprecated Prefer getSmsAddonPurchaseConfirmLabel(isYearly). */
export const SMS_ADDON_PURCHASE_CONFIRM_LABEL =
	getSmsAddonPurchaseConfirmLabel(false);

/**
 * Add-on state for a current-subscription payload.
 * Tri-state: the flag is absent, present and false, or present and true.
 * Absent means the backend does not report the add-on at all, so neither the
 * purchase surface nor the entitlement may be inferred from it.
 *
 * @param {object} currentSubscription state.subscription.currentSubscription
 * @returns {{backendReportsSmsAddon: boolean, hasSmsAddon: boolean}}
 */
export const getSmsAddonState = (currentSubscription) => {
	const subscription = currentSubscription?.subscription || currentSubscription;
	const backendReportsSmsAddon =
		reportsSmsAddonFlag(currentSubscription) ||
		reportsSmsAddonFlag(currentSubscription?.subscription);
	const hasSmsAddon =
		backendReportsSmsAddon &&
		Boolean(
			currentSubscription?.[SMS_ADDON_FLAG_KEY] ||
				subscription?.[SMS_ADDON_FLAG_KEY]
		);
	return { backendReportsSmsAddon, hasSmsAddon };
};
